import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Multi-color verse highlights. Distinct from bookmarks: bookmarks are
 * keepsakes you return to; highlights are an in-flight reading aid.
 *
 * Storage: localStorage map of verseRef → color. Capped at 2000 entries
 * (a serious reader's typical lifetime spread).
 */

export const HIGHLIGHT_COLORS = ['gold', 'rose', 'sage', 'sky'] as const;
export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number];

const STORAGE_KEY = '_hl1';
const MAX_ENTRIES = 2000;

type Map = Record<string, HighlightColor>;

function load(): Map {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export const highlights = signal<Map>(load());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(highlights.value));
  });
}

export function getHighlight(ref: string): HighlightColor | null {
  return highlights.value[ref] ?? null;
}

export function setHighlight(ref: string, color: HighlightColor | null): void {
  const next = { ...highlights.peek() };
  if (color === null) {
    delete next[ref];
  } else {
    next[ref] = color;
  }
  const entries = Object.entries(next);
  if (entries.length > MAX_ENTRIES) {
    // Trim oldest insertion order — Map insertion order via Object iteration
    // is stable in modern V8 but we don't track timestamps; just drop a chunk
    // from the start.
    const trimmed: Map = {};
    for (const [k, v] of entries.slice(-MAX_ENTRIES)) trimmed[k] = v;
    highlights.value = trimmed;
  } else {
    highlights.value = next;
  }
}

export function cycleHighlight(ref: string): HighlightColor | null {
  const current = getHighlight(ref);
  if (!current) {
    const first = HIGHLIGHT_COLORS[0];
    if (first) setHighlight(ref, first);
    return first ?? null;
  }
  const idx = HIGHLIGHT_COLORS.indexOf(current);
  const next = HIGHLIGHT_COLORS[idx + 1] ?? null;
  setHighlight(ref, next);
  return next;
}
